import React from 'react';
import { PRICING } from '../pricing';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SpeedIcon from '@mui/icons-material/Speed';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import QuizIcon from '@mui/icons-material/Quiz';
import LanIcon from '@mui/icons-material/Lan';
import CodeIcon from '@mui/icons-material/Code';
import SchoolIcon from '@mui/icons-material/School';
import ExtensionIcon from '@mui/icons-material/Extension';
import SyncIcon from '@mui/icons-material/Sync';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import LayersIcon from '@mui/icons-material/Layers';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BuildIcon from '@mui/icons-material/Build';

export const interviewPrep = {
  title: "Frontend Interview Preparation",
  subtitle: "Job-Ready Blueprint",
  badge: "RECOMMENDED",
  icon: <SchoolIcon sx={{ fontSize: 40, color: '#f59e0b' }} />,
  description: "150+ exhaustive Q&A scenarios. Curriculum sourced directly from real-world FAANG interview experiences.",
  features: [
    "Machine Coding Challenges",
    "150+ Q&A Scenarios",
    "Behavioral & Cultural QA",
    "Based on Real Interviews"
  ],
  price: PRICING.interviewPrep.price,
  mrp: PRICING.interviewPrep.mrp,
  topics: [
    {
      id: "prep_js_basics",
      title: "JS Basics & Functions",
      desc: "Core JS, Closures, Hoisting, Prototypes, ES6+.",
      badge: "Core",
      icon: <CodeIcon />,
      group: "JS Fundamentals",
      problems: [
        { id: "js_basic_core", title: "JS Core Mechanics", badge: "Engine", desc: "Understanding V8, Execution Context, and Memory.", fileUrl: "/documents/interview/prep_js_core.md" },
        { id: "js_basic_fn", title: "Functions & Closures", badge: "Scope", desc: "Lexical scoping, HOFs, and memory persistence.", fileUrl: "/documents/interview/prep_js_functions.md" },
        { id: "js_basic_proto", title: "Prototypes & Context", badge: "OOP", desc: "Prototypal inheritance and context behavior.", fileUrl: "/documents/interview/prep_js_prototypes.md" },
        { id: "js_basic_es6", title: "Modern ES6+ Features", badge: "Syntax", desc: "Destructuring, rest/spread, and proxy objects.", fileUrl: "/documents/interview/prep_js_es6.md" },
        { id: "js_basic_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Highest-frequency questions on JS fundamentals.", fileUrl: "/documents/interview/prep_js_basics_faq.md" }
      ]
    },
    {
      id: "prep_js_async",
      title: "JS Async & Event Loop",
      desc: "Event Loop, Macrotasks vs Microtasks, and V8 mechanics.",
      badge: "Async",
      icon: <SyncIcon />,
      group: "JS Fundamentals",
      problems: [
        { id: "async_timers", title: "Timers & Event Loop", badge: "Mechanics", desc: "setTimeout, setInterval, and Macrotasks.", fileUrl: "/documents/interview/prep_async_timers.md" },
        { id: "async_apis", title: "Fetch API & Networking", badge: "Network", desc: "Handling HTTP requests, CORS, and fetch.", fileUrl: "/documents/interview/prep_async_apis.md" },
        { id: "prep_http_codes", title: "HTTP Status Codes & Signals", badge: "Signals", desc: "Mastering the mood of server responses.", fileUrl: "/documents/frontend_hld/fe_arch_http.md" },
        { id: "async_generators", title: "Iterators & Generators", badge: "Advanced", desc: "Yielding values and iterable protocols.", fileUrl: "/documents/interview/prep_async_generators.md" },
        { id: "async_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Highest-frequency async JS questions.", fileUrl: "/documents/interview/prep_js_async_faq.md" }
      ]
    },
    {
      id: "prep_promises_mastery",
      title: "Understanding Promises",
      desc: "Master Promise Lifecycle, polyfills (all, race, any), and Microtasks.",
      badge: "Async",
      icon: <SyncAltIcon />,
      fileUrl: "/documents/interview/prep_async_promises.md",
      group: "JS Fundamentals",
      problems: []
    },
    {
      id: "prep_js_architecture",
      title: "JS Architecture & Patterns",
      desc: "Master common design patterns used in massive frontend apps.",
      badge: "Architect",
      icon: <DashboardIcon />,
      group: "Advanced Patterns",
      problems: [
        { id: "arch_patterns", title: "Design Patterns", badge: "Core", desc: "Singleton, Factory, Observer, and Module patterns.", fileUrl: "/documents/interview/prep_js_design_patterns.md" },
        { id: "arch_mech", title: "Performance Mechanics", badge: "Expert", desc: "Rendering pipeline optimizations and leak debugging.", fileUrl: "/documents/interview/prep_js_performance.md" },
        { id: "arch_build", title: "Build Tools & Tooling", badge: "DevOps", desc: "Webpack, Babel internals, and module bundling.", fileUrl: "/documents/interview/prep_js_build_tools.md" },
        { id: "js_arch_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "High-signal questions on JS architecture.", fileUrl: "/documents/interview/prep_js_architecture_faq.md" }
      ]
    },
    {
      id: "prep_js_optimization",
      title: "Optimization Techniques",
      desc: "Debounce, Throttle, Memoization, and DOM Performance.",
      badge: "Perf",
      icon: <SpeedIcon />,
      fileUrl: "/documents/interview/prep_js_optimization.md",
      group: "Advanced Patterns",
      problems: []
    },
    {
      id: "prep_js_dom_browser",
      title: "JS Browser & DOM",
      desc: "Rendering Pipeline, Objects, Events, CSS-in-JS.",
      badge: "Browser",
      fileUrl: "/documents/interview/prep_js_dom_browser.md",
      icon: <LayersIcon />,
      group: "JS Fundamentals",
      problems: []
    },
    {
      id: "prep_js_ecosystem",
      title: "JS Ecosystem & Security",
      desc: "Arrays, Polyfills, Security, Testing, Tooling.",
      badge: "Ecosystem",
      icon: <SchoolIcon />,
      group: "JS Fundamentals",
      problems: [
        { id: "eco_security", title: "Web Security & Auth", badge: "Security", desc: "XSS, CSRF, JWTs, and secure cookie handling.", fileUrl: "/documents/interview/prep_eco_security.md" },
        { id: "eco_testing", title: "Testing Mastery", badge: "Jest", desc: "Mocks, stubs, spies, and achieving test coverage.", fileUrl: "/documents/interview/prep_eco_testing.md" },
        { id: "eco_tooling", title: "NPM & Tooling", badge: "NPM", desc: "Dependency management, and resolving conflicts.", fileUrl: "/documents/interview/prep_eco_tooling.md" },
        { id: "eco_polyfills", title: "Array Methods & Polyfills", badge: "Arrays", desc: "Writing map, filter, and reduce from scratch.", fileUrl: "/documents/interview/prep_eco_polyfills.md" },
        { id: "js_eco_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Common questions on the broader JS ecosystem.", fileUrl: "/documents/interview/prep_js_ecosystem_faq.md" }
      ]
    },
    {
      id: "js_interview",
      title: "Expert JS Masterclass",
      desc: "Senior-level output questions with visualizations.",
      badge: "Expert",
      fileUrl: "/documents/javascript/js_interview_questions.md",
      icon: <QuizIcon />,
      group: "Advanced Patterns",
      problems: []
    },
    {
      id: "prep_react_core",
      title: "React Core Fundamentals",
      desc: "Components, Props, State, Lifecycles, Lists.",
      badge: "React",
      icon: <LayersIcon />,
      group: "React Fundamentals",
      problems: [
        { id: "react_jsx", title: "JSX & Component Philosophy", badge: "Engine", desc: "Babel compilation and the virtual DOM mindset.", fileUrl: "/documents/interview/prep_react_jsx.md" },
        { id: "react_props", title: "Data Flow & Props", badge: "Props", desc: "Unidirectional data flow and prop drilling solutions.", fileUrl: "/documents/interview/prep_react_props.md" },
        { id: "react_state", title: "State & Immutability", badge: "State", desc: "Updating React state strictly without mutation traps.", fileUrl: "/documents/interview/prep_react_state_basics.md" },
        { id: "react_lifecycle", title: "Component Lifecycles", badge: "Class", desc: "Mounting, updating, and unmounting phases.", fileUrl: "/documents/interview/prep_react_lifecycle.md" },
        { id: "react_core_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Highest-frequency questions on React fundamentals.", fileUrl: "/documents/interview/prep_react_core_faq.md" }
      ]
    },
    {
      id: "prep_react_hooks",
      title: "React Hooks & Events",
      desc: "useState, useEffect, Refs, Custom Hooks.",
      badge: "Hooks",
      icon: <ExtensionIcon />,
      group: "React Fundamentals",
      problems: [
        { id: "hook_usestate", title: "The useState Hook", badge: "State", desc: "Batching, functional updates, and lazy initialization.", fileUrl: "/documents/interview/prep_hook_useState.md" },
        { id: "hook_useeffect", title: "The useEffect Hook", badge: "Effects", desc: "Dependency arrays, cleanup actions, and stale closures.", fileUrl: "/documents/interview/prep_hook_useEffect.md" },
        { id: "hook_refs", title: "Refs & Forms", badge: "Forms", desc: "useRef for DOM access and mutable variables.", fileUrl: "/documents/interview/prep_hook_refs.md" },
        { id: "hook_custom", title: "Custom Hooks & Logic Reuse", badge: "Architecture", desc: "Abstracting reusable stateful logic.", fileUrl: "/documents/interview/prep_hook_custom.md" },
        { id: "hook_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Mastering hooks-based interview scenarios.", fileUrl: "/documents/interview/prep_react_hooks_faq.md" }
      ]
    },
    {
      id: "prep_react_advanced",
      title: "React Architecture",
      desc: "Composition, Router, HOC, Context, Redux.",
      badge: "Architecture",
      icon: <LanIcon />,
      group: "Advanced Patterns",
      problems: [
        { id: "arch_context", title: "Context API & Global State", badge: "Context", desc: "When and why to use React Context over prop drilling.", fileUrl: "/documents/interview/prep_arch_context.md" },
        { id: "arch_hoc", title: "Component Composition & HOCs", badge: "Patterns", desc: "Enhancing components with Higher Order abstractions.", fileUrl: "/documents/interview/prep_arch_hoc.md" },
        { id: "arch_errors", title: "Error Boundaries & React Router", badge: "Router", desc: "Catching render errors and client-side routing logic.", fileUrl: "/documents/interview/prep_arch_errors.md" },
        { id: "arch_redux", title: "Global State & Redux", badge: "Redux", desc: "Actions, Reducers, middleware, and the Redux toolkit.", fileUrl: "/documents/interview/prep_arch_redux.md" },
        { id: "react_arch_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Deep questions on React architecture and scaling.", fileUrl: "/documents/interview/prep_react_architecture_faq.md" }
      ]
    },
    {
      id: "react_interview_qa",
      title: "Senior React Masterclass",
      desc: "React 18 internals, SSR, Fiber, and Concurrency.",
      badge: "Expert",
      icon: <QuizIcon />,
      group: "Advanced Patterns",
      problems: [
        { id: "master_react18", title: "React 18 & Concurrency", badge: "Core", desc: "Automatic batching, Transitions, and Suspense.", fileUrl: "/documents/interview/prep_master_react18.md" },
        { id: "master_ssr", title: "SSR & Hydration", badge: "Server", desc: "Server-side rendering and progressive hydration.", fileUrl: "/documents/interview/prep_master_ssr.md" },
        { id: "master_perf", title: "React Performance & VDOM", badge: "Performance", desc: "Memoization, virtualization, and bypassing the VDOM.", fileUrl: "/documents/interview/prep_master_perf.md" },
        { id: "master_patterns", title: "Advanced Patterns & Testing", badge: "Architecture", desc: "Compound components, render props, and testing.", fileUrl: "/documents/interview/prep_master_patterns.md" }
      ]
    },
    {
      id: "prep_css_mastery",
      title: "CSS Architecture & Mastery",
      desc: "Layout engines, Box Model, Specificity, Variables, and CSS Performance.",
      badge: "CSS",
      icon: <DashboardIcon />,
      group: "CSS Mastery",
      problems: [
        { id: "css_1", title: "Flexbox vs Grid", badge: "Layout", desc: "1D vs 2D layout planning and responsiveness.", fileUrl: "/documents/interview/prep_css_layout.md" },
        { id: "css_2", title: "The Box Model", badge: "Core", desc: "Margin collapsing, box-sizing rules, and padding.", fileUrl: "/documents/interview/prep_css_box_model.md" },
        { id: "css_3", title: "Specificity & Cascade", badge: "Logic", desc: "Calculating selector weight and overriding rules.", fileUrl: "/documents/interview/prep_css_specificity.md" },
        { id: "css_4", title: "Modern CSS Architecture", badge: "Modern", desc: "BEM, CSS-in-JS, and styled-components tradeoffs.", fileUrl: "/documents/interview/prep_css_modern.md" },
        { id: "css_5", title: "CSS Performance Mechanics", badge: "Perf", desc: "Critical CSS, repaint vs reflow, and hardware acceleration.", fileUrl: "/documents/interview/prep_css_perf.md" },
        { id: "css_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Common CSS questions asked in UI interviews.", fileUrl: "/documents/interview/prep_css_mastery_faq.md" }
      ]
    },
    {
      id: "prep_ts_fundamentals",
      title: "TypeScript Engineering",
      desc: "Core Types, Generics, Utility Types, and precise strongly typed React.",
      badge: "TS",
      icon: <CodeIcon />,
      group: "TypeScript",
      problems: [
        { id: "ts_1", title: "Core Types & Interfaces", badge: "Basics", desc: "Type declarations, unions, intersections, and unknown.", fileUrl: "/documents/interview/prep_ts_types.md" },
        { id: "ts_2", title: "Advanced Generics", badge: "Patterns", desc: "Building strongly-typed programmatic constraints.", fileUrl: "/documents/interview/prep_ts_advanced.md" },
        { id: "ts_3", title: "TypeScript with React", badge: "React", desc: "Typing props, state, event handlers, and refs securely.", fileUrl: "/documents/interview/prep_ts_react.md" },
        { id: "ts_4", title: "Utility Types", badge: "Utilities", desc: "Partial, Pick, Omit, and Record transformations.", fileUrl: "/documents/interview/prep_ts_utility.md" },
        { id: "ts_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Highest-frequency TypeScript interview scenarios.", fileUrl: "/documents/interview/prep_ts_fundamentals_faq.md" }
      ]
    },
    {
      id: "prep_test_qa",
      title: "Testing & Quality Assurance",
      desc: "Unit Testing with Vitest/Jest, React Testing Library, and End-to-End browser tests.",
      badge: "QA",
      icon: <BuildIcon />,
      group: "Testing & QA",
      problems: [
        { id: "test_1", title: "Unit Testing Fundamentals", badge: "Unit", desc: "AAA pattern, pure functions, and testing isolated logic.", fileUrl: "/documents/interview/prep_test_unit.md" },
        { id: "test_2", title: "React Testing Library", badge: "Integration", desc: "Testing component behavior from a user's perspective.", fileUrl: "/documents/interview/prep_test_integration.md" },
        { id: "test_3", title: "End-to-End Testing (E2E)", badge: "E2E", desc: "Cypress/Playwright strategies for critical user flows.", fileUrl: "/documents/interview/prep_test_e2e.md" },
        { id: "test_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Best practices and common QA interview questions.", fileUrl: "/documents/interview/prep_test_qa_faq.md" }
      ]
    },
    {
      id: "prep_web_apis",
      title: "Modern Web APIs & Storage",
      desc: "Intersection Observers, Background threads, IndexedDB, and Storage constraints.",
      badge: "Browser",
      icon: <LanIcon />,
      group: "Web APIs",
      problems: [
        { id: "api_1", title: "Modern Observers & Threads", badge: "APIs", desc: "IntersectionObserver and Web Workers functionality.", fileUrl: "/documents/interview/prep_api_modern.md" },
        { id: "api_2", title: "Web Storage Strategies", badge: "Storage", desc: "LocalStorage, SessionStorage, and IndexedDB limits.", fileUrl: "/documents/interview/prep_api_storage.md" },
        { id: "api_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Questions concerning native browser Web APIs.", fileUrl: "/documents/interview/prep_web_apis_faq.md" }
      ]
    },
    {
      id: "prep_performance",
      title: "Frontend Performance & Web Vitals",
      desc: "LCP, CLS, Interaction to Next Paint (INP), and Chrome DevTools. ",
      badge: "Performance",
      icon: <SpeedIcon />,
      group: "Web APIs",
      problems: [
        { id: "perf_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Deep dive on Core Web Vitals, CDN caching, and techniques.", fileUrl: "/documents/interview/prep_performance_faq.md" }
      ]
    },
    {
      id: "prep_a11y",
      title: "Web Accessibility (a11y)",
      desc: "ARIA roles, Semantic HTML, Screen Readers, and focus management.",
      badge: "a11y",
      icon: <AccessibilityNewIcon />,
      group: "Web APIs",
      problems: [
        { id: "a11y_basics", title: "Accessibility Basics", badge: "Core", desc: "Keyboard navigation, screen readers, and WCAG standards.", fileUrl: "/documents/interview/prep_a11y_basics.md" },
        { id: "a11y_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Ensuring web applications are usable by everyone.", fileUrl: "/documents/interview/prep_a11y_faq.md" }
      ]
    },
    {
      id: "prep_next_framework",
      title: "Next.js & Meta-Frameworks",
      desc: "Server vs Client Components, App Router, SSR, SSG, and ISR.",
      badge: "Next.js",
      icon: <SyncAltIcon />,
      group: "Meta-Frameworks",
      problems: [
        { id: "next_1", title: "App Router & RSC", badge: "Core", desc: "Server Components vs Client Components architecture.", fileUrl: "/documents/interview/prep_next_app_router.md" },
        { id: "next_2", title: "Data Fetching & ISR", badge: "Cache", desc: "Static vs dynamic rendering and caching mechanisms.", fileUrl: "/documents/interview/prep_next_data_fetching.md" },
        { id: "next_faq", title: "Comprehensive Q&A", badge: "FAQ", desc: "Crucial framework questions often asked by Meta/Vercel.", fileUrl: "/documents/interview/prep_next_faq.md" }
      ]
    },
    {
      id: "interview_sys_design",
      title: "Frontend System Design",
      desc: "Architect scalable, highly available UI components and data flows natively.",
      badge: "Architecture",
      icon: <SyncAltIcon />,
      group: "Senior Engineering",
      problems: [
        { id: "sys_autocomplete", title: "Design an Autocomplete Widget", badge: "Design", desc: "Debouncing, trie networks, and caching API results.", fileUrl: "/documents/interview/prep_sys_autocomplete.md" },
        { id: "sys_infinite_feed", title: "Design an Infinite News Feed", badge: "Design", desc: "Virtualization, pagination, and shifting render windows.", fileUrl: "/documents/interview/prep_sys_infinite_feed.md" },
        { id: "sys_chat", title: "Design a High-Frequency Live Chat", badge: "Design", desc: "WebSockets, optimisitic UI, and message ordering.", fileUrl: "/documents/interview/prep_sys_chat.md" },
        { id: "sys_video_player", title: "Design a Video Streaming Player", badge: "Design", desc: "Buffering chunks, adaptive bitrates, and custom UI controls.", fileUrl: "/documents/interview/prep_sys_video_player.md" },
        { id: "sys_collaborative", title: "Design a Collaborative Document", badge: "Design", desc: "Operational transformation and CRDT algorithms.", fileUrl: "/documents/interview/prep_sys_collaborative.md" }
      ]
    },
    {
      id: "interview_machine_coding",
      title: "Machine Coding Implementations",
      desc: "Write complex JavaScript APIs purely from scratch flawlessly.",
      badge: "Coding",
      icon: <CodeIcon />,
      group: "Senior Engineering",
      problems: [
        { id: "machine_promise", title: "Implement a Custom Promise", badge: "Coding", desc: "Writing then, catch, and fully resolving state loops.", fileUrl: "/documents/interview/prep_machine_promise.md" },
        { id: "machine_pubsub", title: "Implement an Event Bus / PubSub", badge: "Coding", desc: "Publishing nodes and unsubscribing complex event listeners.", fileUrl: "/documents/interview/prep_machine_pubsub.md" },
        { id: "machine_reduce", title: "Implement Array.reduce natively", badge: "Coding", desc: "Handling accumulators, initial values, and edge cases.", fileUrl: "/documents/interview/prep_machine_reduce.md" },
        { id: "machine_deepclone", title: "Implement a Robust Deep Clone", badge: "Coding", desc: "Circumventing circular references and object types.", fileUrl: "/documents/interview/prep_machine_deepclone.md" },
        { id: "machine_curry", title: "Implement Generic Currying", badge: "Coding", desc: "Binding variables until function arity is satisfied.", fileUrl: "/documents/interview/prep_machine_curry.md" }
      ]
    },
    {
      id: "interview_leadership",
      title: "Engineering Leadership",
      desc: "Behavioral interviews explicitly navigating tech debt and team conflicts.",
      badge: "Leadership",
      icon: <PsychologyIcon />,
      group: "Senior Engineering",
      problems: [
        { id: "leadership_patterns", title: "Leadership & Architecture Patterns", badge: "Guide", desc: "Leading migrations, RFCs, and mentoring junior engineers.", fileUrl: "/documents/interview/prep_leadership.md" },
        { id: "behavioral_faq", title: "Comprehensive Behavioral Q&A", badge: "FAQ", desc: "Handling conflicts, failing projects, and constructive feedback.", fileUrl: "/documents/interview/prep_behavioral_faq.md" },
        { id: "leadership_cross_team", title: "Navigating Cross-Team Conflicts", badge: "Guide", desc: "Aligning product, design, and multiple engineering squads.", fileUrl: "/documents/interview/prep_leadership_cross_team.md" }
      ]
    }
  ]
};
